// import NewLayout from '@components/NewLayout';
import Qrform from "@components/Qrform";
import { MessageForm } from "@components/shortURL/MessageForm";
import React, { useState } from "react";
import type { FormData } from "types/index";
import { isPhnNoWithCountryCode } from "@utils/common";
import { checkStatus, parseJSON } from "utils/fetch";
import { insertQRGeneratorDataInDB } from "database/supabase/freeTools";

const canRunOnClient = () => {
    return typeof window !== "undefined";
};
interface ShortUrlProps {
    ref?: React.LegacyRef<HTMLDivElement>;
    qrform?: boolean;
}

const QrGenerator = React.forwardRef<HTMLDivElement, ShortUrlProps>((props, ref) => {
    // const {ref} = props
    const [isOpen, setOpen] = useState<boolean>(false);
    const [qrCodeUrl, setQRCodeUrl] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [isURLCopied, setURLCopy] = useState(false);
    const [shortLink, setLink] = useState("");
    const baseURL = canRunOnClient() ? "wame.pro" : "";
    const onOpen = () => {
        setOpen(true);
    };
    const onClose = () => {
        setOpen(false);
    };

    const [formData, setFormData] = useState<FormData>({
        country_code: "91",
        phn_no: "",
        message: "",
        isValidPhnNo: false,
        isValidForm: true,
        isEmojiPopupOpen: false,
    });

    const handleFormInputChange = (userInput: Partial<FormData>) => {
        if (userInput["phn_no"]) {
            const data = {
                ...formData,
                ...userInput,
                isValidForm: true,
            };
            setFormData(data);
        } else {
            const data = {
                ...formData,
                ...userInput,
            };
            setFormData(data);
        }
    };
    const validateForm = () => {
        if (formData.isValidPhnNo) {
            return true;
        }
        setFormData({
            ...formData,
            isValidForm: false,
        });
        return false;
    };
    const handleGenerateLink = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        if (validateForm()) {
            const { phn_no, country_code, message } = formData;
            const waNumberFormat = `${isPhnNoWithCountryCode(phn_no) ? phn_no : country_code + phn_no}`;
            const payload = { whatsappNumber: waNumberFormat, message: message };

            try {
                setURLCopy(false);
                setLoading(true);
                const data = await fetch("/api/v1/shorturl/uploadShortUrl", {
                    method: "POST",
                    body: JSON.stringify(payload),
                })
                    .then(checkStatus)
                    .then(parseJSON);
                setLoading(false);
                setQRCodeUrl(data.dataURL);
                setLink(`${baseURL}/${data.uid}`);
                onOpen();
            } catch (e) {
                console.log("error", e);
                setLoading(false);
                setQRCodeUrl("");
                setLink("");
            } finally {
                insertQRGeneratorDataInDB(payload);
            }
        }
    };

    const getShortLink = (shortLink: string) => {
        if (canRunOnClient()) {
            return `${window.location.protocol}//${shortLink}`;
        }
    };

    return (
        <div ref={ref} className={(isOpen ? "overflow-hidden " : "overflow-auto ") + ""}>
            {!props.qrform ? (
                <div className="mb-12 " style={{ backgroundColor: "#F0FFF4" }}>
                    <div className="flex justify-center items-center lg:items-stretch md:items-center flex-col lg:flex-row pt-20 pb-24 ">
                        <MessageForm
                            onGenerateLink={handleGenerateLink}
                            handleFormInputChange={handleFormInputChange}
                            data={formData}
                            isLoading={isLoading}
                            validateForm={validateForm}
                        />
                    </div>
                </div>
            ) : (
                <Qrform onGenerateLink={handleGenerateLink} handleFormInputChange={handleFormInputChange} data={formData} isLoading={isLoading} validateForm={validateForm} />
            )}

            <div
                id="modal-popup-shorturl"
                className={(isOpen ? "flex" : "hidden") + " justify-center top-0 bottom-0 right-0 left-0 fixed z-10 w-screen overflow-y-scroll pb-14 pt-3 bg-black bg-opacity-50"}
                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    const modal = document.getElementById("modal-popup-shorturl");
                    if (e.target === modal) {
                        onClose();
                    }
                }}
            >
                <div className="flex justify-center relative h-fit max-w-md px-2.5">
                    <div className="flex flex-col bg-white rounded-xl min-w-full lg:min-w-md">
                        <div className="flex justify-end w-full p-3.5">
                            <img
                                className="w-4 h-4 cursor-pointer"
                                onClick={() => {
                                    onClose();
                                }}
                                src="/close.png"
                                data-cy="close-modal"
                                alt="close button"
                            />
                        </div>
                        <div>
                            <div className="font-bold text-xl mb-4 ml-6 mr-8">Your WhatsApp Link Is Ready!</div>
                        </div>
                        <div className="bg-active flex flex-col items-center justify-center gap-y-3 mx-6">
                            <p>
                                Share the WhatsApp link in your social channels, embed it in your outgoing emails, and add it to your webpages or blog. Help your existing and
                                potential customers reach you directly in just a click! You can also download the QR code and display or share it anywhere you want to.
                            </p>
                            <div className="flex justify-center items-center break-words flex-wrap">
                                <div className="h-10 w-10">
                                    <img src="https://assets.gallabox.com/gb-home/home/whatsapp-circle.png" alt="whatsapp logo" />
                                </div>
                                <a href={getShortLink(shortLink)} target="_blank" className="font-medium text-xl ml-2.5 break-all text-center" rel="noreferrer">
                                    {shortLink}
                                </a>
                            </div>
                            <img src={qrCodeUrl} className="h-60 w-60" />
                        </div>
                        <div className="flex flex-col mx-6">
                            <hr className="m-0" />
                            <div className="flex justify-end gap-x-2 my-4">
                                <button
                                    onClick={() => {
                                        setURLCopy(true);
                                        navigator.clipboard.writeText(shortLink);
                                    }}
                                    className={
                                        "hover:bg-green-medium-dark border-slate-300  rounded-full font-semibold py-1.5 px-3 text-white " +
                                        (isURLCopied ? "bg-green-medium-dark" : "bg-green-dark")
                                    }
                                    data-cy="copy-short-link"
                                >
                                    {isURLCopied ? "Copied!" : "Copy link"}
                                </button>
                                <a
                                    href={qrCodeUrl}
                                    className="hover:bg-gray-light hover:no-underline bg-gray-light border-slate-300 rounded-full font-semibold py-1.5 px-3 text-black"
                                    download="whatsappqrcode.png"
                                    data-cy="download-qr-code"
                                >
                                    Download QR
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
QrGenerator.displayName = "QrGenerator";
export default QrGenerator;

QrGenerator.displayName = "QrGenerator";
